import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Briefcase, Plus, CaretDown, CaretUp, CheckCircle, PlayCircle, ArrowCounterClockwise,
  Trash, ChatCircleText, PaperPlaneRight, WarningCircle, CalendarBlank, Sparkle, ArrowsClockwise, Robot,
} from "@phosphor-icons/react";
import { api, formatApiError, formatDateTime, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { VoiceTypingButton } from "../components/VoiceTypingButton";
import { VoiceRecorderButton, VoicePlayer } from "../components/VoiceRecorder";

const PRIORITY = {
  high: { label: "High", cls: "bg-red-100 text-red-700 border-red-300", bar: "border-l-red-500" },
  medium: { label: "Medium", cls: "bg-amber-100 text-amber-700 border-amber-300", bar: "border-l-amber-400" },
  low: { label: "Low", cls: "bg-green-100 text-green-700 border-green-300", bar: "border-l-green-500" },
};
const STATUS = {
  pending: { label: "Pending", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  awaiting_approval: { label: "Needs approval", cls: "bg-amber-400 text-amber-950 border-amber-500 animate-pulse" },
  done: { label: "Done", cls: "bg-green-100 text-green-700 border-green-300" },
};
const AVATAR_COLORS = ["#DC2626", "#2563EB", "#7C3AED", "#D97706", "#0891B2", "#16A34A", "#DB2777"];

const isOverdue = (n) => n.due_date && n.status !== "done" && n.due_date < new Date().toISOString().slice(0, 10);
const fmtDue = (d) => d.split("-").reverse().join("/");

const TaskCard = ({ n, user, isAdmin, busy, act, onReplySent }) => {
  const [open, setOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const replyRef = useRef(null);
  const overdue = isOverdue(n);
  const updates = n.replies || [];

  const setStatus = (status, msg) => act(() => api.post(`/notes/${n.id}/status`, { status }), msg);
  const sendReply = () =>
    act(async () => {
      await api.post(`/notes/${n.id}/reply`, { text: replyText });
      setReplyText("");
      onReplySent?.();
    }, "Update sent");
  const sendVoice = (blob) =>
    act(async () => {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      await api.post(`/notes/${n.id}/voice`, fd);
    }, "Voice message sent");

  return (
    <div data-testid={`task-${n.id}`}
      className={`border border-border border-l-4 ${PRIORITY[n.priority]?.bar} rounded-lg bg-card p-3 space-y-2 ${n.status === "done" ? "opacity-60" : ""}`}>
      <div className={`text-sm font-medium leading-snug ${n.status === "done" ? "line-through" : ""}`}>{n.text}</div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        <span className={`font-mono font-semibold px-1.5 py-0.5 rounded border ${STATUS[n.status]?.cls}`}>{STATUS[n.status]?.label}</span>
        {n.ai_generated && <span className="font-semibold px-1.5 py-0.5 rounded border bg-violet-100 text-violet-700 border-violet-300 inline-flex items-center gap-0.5"><Robot size={11} weight="fill" /> AI</span>}
        <span className={`font-mono font-semibold px-1.5 py-0.5 rounded border ${PRIORITY[n.priority]?.cls}`}>{PRIORITY[n.priority]?.label}</span>
        {n.due_date && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold ${overdue ? "bg-red-600 text-white border-red-700" : "bg-muted text-muted-foreground border-border"}`}>
            {overdue ? <WarningCircle size={11} weight="fill" /> : <CalendarBlank size={11} />} {fmtDue(n.due_date)}{overdue ? " · OVERDUE" : ""}
          </span>
        )}
        <span className="text-muted-foreground ml-auto">by {n.created_by_role === "super_admin" ? "Admin" : n.created_by_name}</span>
      </div>
      <div className="flex items-center gap-1.5 pt-0.5">
        {n.status === "awaiting_approval" && isAdmin && (
          <Button data-testid={`task-approve-${n.id}`} size="sm" disabled={busy}
            className="h-8 px-3 rounded-full text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95"
            onClick={() => act(() => api.post(`/notes/${n.id}/approve`), "Task approved and closed")}>
            ✓ Approve & Close
          </Button>
        )}
        {n.status === "pending" && (
          <Button data-testid={`task-start-${n.id}`} size="sm" variant="outline" disabled={busy}
            className="h-8 px-2.5 rounded-full text-[11px] font-semibold text-blue-700 border-blue-300 active:scale-95"
            onClick={() => setStatus("in_progress", "Marked as in progress")}>
            <PlayCircle size={14} className="mr-1" /> Start
          </Button>
        )}
        {n.status !== "done" && n.status !== "awaiting_approval" ? (
          <Button data-testid={`task-done-${n.id}`} size="sm" variant="outline" disabled={busy}
            className="h-8 px-2.5 rounded-full text-[11px] font-semibold text-green-700 border-green-300 active:scale-95"
            onClick={() => setStatus("done", "Task complete!")}>
            <CheckCircle size={14} className="mr-1" /> Done
          </Button>
        ) : n.status === "done" ? (
          <Button data-testid={`task-reopen-${n.id}`} size="sm" variant="outline" disabled={busy}
            className="h-8 px-2.5 rounded-full text-[11px] font-semibold text-amber-700 border-amber-300 active:scale-95"
            onClick={() => setStatus("pending")}>
            <ArrowCounterClockwise size={13} className="mr-1" /> Reopen
          </Button>
        ) : null}
        <button data-testid={`task-updates-toggle-${n.id}`} onClick={() => setOpen(!open)}
          className={`h-8 px-2.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 border transition-colors active:scale-95 ${open ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
          <ChatCircleText size={14} /> {updates.length > 0 ? `${updates.length} Updates` : "Update"}
        </button>
        {(isAdmin || n.created_by === user.id) && (
          <button data-testid={`task-delete-${n.id}`} disabled={busy}
            onClick={() => window.confirm("Delete this task?") && act(() => api.delete(`/notes/${n.id}`), "Task deleted")}
            className="ml-auto text-red-500 hover:bg-red-50 rounded-full p-1.5 active:scale-90"><Trash size={15} /></button>
        )}
      </div>
      {open && (
        <div className="space-y-2 pt-1">
          {updates.length > 0 && (
            <div className="space-y-1.5 border-l-2 border-primary pl-2.5 max-h-48 overflow-y-auto">
              {updates.map((r, i) => (
                <div key={i} className="text-xs">
                  <span className="font-semibold">{r.by_role === "super_admin" ? "Admin" : r.by_name}:</span>{" "}
                  {r.type === "voice" ? <VoicePlayer url={`/notes/${n.id}/voice/${r.voice_id}`} /> : r.text}
                  <span className="text-muted-foreground font-mono text-[9px] ml-1">{formatDateTime(r.at)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Input ref={replyRef} data-testid={`task-reply-input-${n.id}`} value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a progress update..." className="h-9 text-sm flex-1" />
            <VoiceTypingButton inputRef={replyRef} onText={setReplyText} />
            <VoiceRecorderButton disabled={busy} onRecorded={sendVoice} />
            <Button data-testid={`task-reply-send-${n.id}`} size="sm" className="h-9 px-3 rounded-full" disabled={!replyText.trim() || busy} onClick={sendReply}>
              <PaperPlaneRight size={15} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeSection = ({ name, role, tasks, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const active = tasks.filter((t) => t.status !== "done").length;
  const overdue = tasks.filter(isOverdue).length;
  const color = AVATAR_COLORS[(name || "").length % AVATAR_COLORS.length];
  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      <button data-testid={`emp-section-${name?.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3.5 py-3 hover:bg-muted/50 transition-colors">
        <span className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: color }}>
          {(name || "?").charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 text-left">
          <span className="block font-semibold text-sm truncate">{name}</span>
          <span className="block text-[10px] text-muted-foreground">{role ? ROLE_LABELS[role] || role : ""}</span>
        </span>
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          {overdue > 0 && <span className="text-[10px] font-mono font-bold bg-red-600 text-white rounded-full px-2 py-0.5">{overdue} overdue</span>}
          {active > 0
            ? <span className="text-[10px] font-mono font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5">{active} active</span>
            : <span className="text-[10px] font-mono font-bold bg-green-100 text-green-800 border border-green-300 rounded-full px-2 py-0.5">all done</span>}
          {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
};

export default function Work() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [notes, setNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const textRef = useRef(null);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const aiRef = useRef(null);
  const [brief, setBrief] = useState(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefBusy, setBriefBusy] = useState(false);

  const load = () => api.get("/notes").then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isAdmin) api.get("/notes/team-members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addTask = () =>
    act(async () => {
      await api.post("/notes", { text, priority, due_date: dueDate || null, assigned_to: isAdmin && assignTo ? assignTo : null });
      setText(""); setDueDate(""); setAssignTo(""); setPriority("medium"); setCreateOpen(false);
    }, "Task assigned");

  const aiParse = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/ai/parse-task", { text: aiText });
      setText(r.data.text);
      setPriority(r.data.priority);
      setDueDate(r.data.due_date || "");
      setAssignTo(r.data.assigned_to || "");
      setCreateOpen(true);
      setAiText("");
      toast.success(r.data.assigned_to_name ? `Task drafted for ${r.data.assigned_to_name} — review and add` : "Task drafted — review and add");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  const loadBrief = async (force) => {
    setBriefBusy(true);
    try {
      const r = await api.get("/ai/daily-summary", { params: force ? { force: true } : {} });
      setBrief(r.data);
      setBriefOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBriefBusy(false); }
  };

  const counts = useMemo(() => ({
    active: notes.filter((n) => n.status !== "done").length,
    pending: notes.filter((n) => n.status === "pending").length,
    in_progress: notes.filter((n) => n.status === "in_progress").length,
    awaiting_approval: notes.filter((n) => n.status === "awaiting_approval").length,
    overdue: notes.filter(isOverdue).length,
    done: notes.filter((n) => n.status === "done").length,
  }), [notes]);

  const filtered = useMemo(() => {
    let list = notes;
    if (filter === "active") list = notes.filter((n) => n.status !== "done");
    else if (filter === "overdue") list = notes.filter(isOverdue);
    else if (filter !== "all") list = notes.filter((n) => n.status === filter);
    const pRank = { high: 0, medium: 1, low: 2 };
    return [...list].sort((a, b) =>
      (isOverdue(b) - isOverdue(a)) || (pRank[a.priority] - pRank[b.priority]) || (b.created_at > a.created_at ? 1 : -1));
  }, [notes, filter]);

  const groups = useMemo(() => {
    if (!isAdmin) return null;
    const map = new Map();
    filtered.forEach((n) => {
      const k = n.assigned_to;
      if (!map.has(k)) map.set(k, { name: n.assigned_to_name, tasks: [] });
      map.get(k).tasks.push(n);
    });
    const roleOf = (id) => members.find((m) => m.id === id)?.role;
    return [...map.entries()].map(([id, g]) => ({ id, role: roleOf(id), ...g }));
  }, [filtered, isAdmin, members]);

  const CHIPS = [
    ["active", "Active", counts.active, "#D97706"],
    ["awaiting_approval", "Approval", counts.awaiting_approval, "#F59E0B"],
    ["pending", "Pending", counts.pending, "#64748B"],
    ["in_progress", "In progress", counts.in_progress, "#2563EB"],
    ["overdue", "Overdue", counts.overdue, "#DC2626"],
    ["done", "Done", counts.done, "#16A34A"],
    ["all", "All", notes.length, "#0F172A"],
  ];

  const cardProps = { user, isAdmin, busy, act };

  return (
    <div className="space-y-4 stagger-in pb-24" data-testid="work-page">
      <div className="flex items-center gap-2.5">
        <span className="w-10 h-10 rounded-xl bg-brand-600 text-white flex items-center justify-center shadow-md shadow-brand-600/25"><Briefcase size={22} weight="bold" /></span>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900">Work hub</h1>
          <p className="text-xs text-muted-foreground">{isAdmin ? "All team tasks and progress in one place" : "Your tasks and progress updates"}</p>
        </div>
      </div>

      <div className="rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50 p-3 space-y-2" data-testid="ai-assistant-card">
        <div className="flex items-center gap-1.5 text-violet-800">
          <Sparkle size={16} weight="fill" />
          <span className="text-xs font-bold uppercase tracking-wide">AI Task Assistant</span>
          <span className="text-[10px] text-violet-500 hidden sm:inline">— speak or type, AI creates the task</span>
        </div>
        <div className="flex gap-2">
          <Input ref={aiRef} data-testid="ai-task-input" value={aiText} onChange={(e) => setAiText(e.target.value)}
            placeholder={isAdmin ? 'e.g. "Tanu to check indent for 5 files tomorrow"' : 'e.g. "Visit the bank on Friday to confirm status"'}
            className="h-10 bg-white border-violet-200" onKeyDown={(e) => e.key === "Enter" && aiText.trim() && !aiBusy && aiParse()} />
          <VoiceTypingButton inputRef={aiRef} onText={setAiText} />
          <Button data-testid="ai-parse-button" size="sm" disabled={!aiText.trim() || aiBusy} onClick={aiParse}
            className="h-10 px-3.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold active:scale-95">
            {aiBusy ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          </Button>
        </div>
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="ai-briefing-card">
          <button data-testid="ai-briefing-toggle" onClick={() => (brief ? setBriefOpen(!briefOpen) : loadBrief(false))}
            className="w-full flex items-center gap-2 px-3.5 py-3 hover:bg-muted/50 transition-colors">
            <Robot size={18} weight="fill" className="text-violet-600" />
            <span className="font-heading font-bold text-sm">Today's AI briefing</span>
            <span className="ml-auto flex items-center gap-2">
              {briefBusy && <ArrowsClockwise size={15} className="animate-spin text-muted-foreground" />}
              {brief && briefOpen ? <CaretUp size={15} /> : <CaretDown size={15} />}
            </span>
          </button>
          {briefOpen && brief && (
            <div className="px-4 pb-3 space-y-2">
              <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-briefing-text">{brief.text}</p>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground font-mono">Generated: {formatDateTime(brief.generated_at)}</span>
                <Button data-testid="ai-briefing-refresh" variant="ghost" size="sm" className="h-7 px-2 text-[11px]" disabled={briefBusy} onClick={() => loadBrief(true)}>
                  <ArrowsClockwise size={13} className="mr-1" /> Refresh
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {CHIPS.map(([key, label, count, color]) => (
          <button key={key} data-testid={`work-filter-${key}`} onClick={() => setFilter(key)}
            className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-xs font-semibold transition-all active:scale-95 ${filter === key ? "text-white shadow-sm" : "bg-card text-foreground border-border hover:bg-muted"}`}
            style={filter === key ? { backgroundColor: color, borderColor: color } : {}}>
            {label}
            <span className={`font-mono text-[10px] px-1.5 rounded-full ${filter === key ? "bg-white/25" : "bg-muted"}`}>{count}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-muted-foreground" data-testid="work-empty">
          No tasks match this filter
        </div>
      )}

      {isAdmin ? (
        <div className="space-y-3">
          {groups.map((g) => (
            <EmployeeSection key={g.id} name={g.name} role={g.role} tasks={g.tasks} defaultOpen={g.tasks.some((t) => t.status !== "done")}>
              {g.tasks.map((n) => <TaskCard key={n.id} n={n} {...cardProps} />)}
            </EmployeeSection>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((n) => <TaskCard key={n.id} n={n} {...cardProps} />)}
        </div>
      )}

      <button data-testid="work-fab" onClick={() => setCreateOpen(true)} title="New task" aria-label="New task"
        className="fixed bottom-24 md:bottom-8 right-5 md:right-8 z-40 w-14 h-14 rounded-2xl bg-brand-600 text-white shadow-xl shadow-brand-600/30 flex items-center justify-center active:scale-90 hover:scale-105 transition-transform">
        <Plus size={26} weight="bold" />
      </button>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-xl" data-testid="work-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create task</DialogTitle>
            <DialogDescription className="text-xs">Describe the task, then set priority and due date</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input ref={textRef} data-testid="work-text-input" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={isAdmin ? "Task for a team member..." : "Your task or plan for tomorrow..."} className="h-10" />
              <VoiceTypingButton inputRef={textRef} onText={setText} />
            </div>
            {isAdmin && (
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger data-testid="work-assign-select" className="h-10 text-sm"><SelectValue placeholder="Assign to (leave empty for yourself)" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({ROLE_LABELS[m.role]})</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="work-priority-select" className="w-[120px] h-10 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">🔴 High</SelectItem>
                  <SelectItem value="medium">🟡 Medium</SelectItem>
                  <SelectItem value="low">🟢 Low</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="work-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="flex-1 h-10 text-sm" />
            </div>
            <Button data-testid="work-add-button" className="w-full h-10 rounded-full font-semibold active:scale-95" disabled={!text.trim() || busy} onClick={addTask}>
              <Plus size={16} className="mr-1" /> Add task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
